// Internal module — import from `../CommunicationStyleService` instead.
//
// 3-tier style resolution (client → template → user → system fallback)
// + the WritingStyleService-merged AI helpers (analyzeWritingSample,
// buildStylePrompt, defaults). Extracted from CommunicationStyleService
// in Sprint 4 #54 to keep the facade under the 800-LOC budget.
//
// Pure functions only: each takes its dependencies explicitly (cache, client,
// logger, aiService) so they're trivially testable without instantiating the
// service. The facade owns the per-instance state (cache map, GraphQL client,
// child logger, aiService reference) and forwards it on each call.

// (createDataUnavailableError import removed — analyzeWritingSample now
// throws plain Error so the UI can surface the real failure cause.)

// System fallback style when nothing else is available
// (merged from CommunicationStyleResolver in Sprint 3)
export const SYSTEM_FALLBACK_STYLE = {
  tone: 'professional',
  formality: 'balanced',
  confidence: 'balanced',
  personalTouch: 'medium',
  technicalLevel: 'intermediate',
  sentenceComplexity: 'moderate',
  commonPhrases: [],
  signatureGreeting: 'Dear {{client_name}},',
  signatureClosing: 'Best regards,',
  analyzedFrom: 'system_default',
  lastUpdated: new Date().toISOString()
};

// ─── 3-tier resolution ──────────────────────────────────────────────────────

/**
 * Main style resolution method - returns the first complete style found
 * Priority: client → template → user default → SYSTEM_FALLBACK_STYLE
 */
export async function resolveStyle(state, client, logger, { clientId, templateId, userId }) {
  try {
    const cacheKey = `${clientId || 'none'}-${templateId || 'none'}-${userId || 'none'}`;
    const cached = getCachedStyle(state, cacheKey);
    if (cached) return cached;

    // Priority 1: Try to get client-specific style
    if (clientId) {
      const clientStyle = await getClientStyle(client, logger, clientId);
      if (clientStyle && isValidStyle(clientStyle)) {
        const result = { ...clientStyle, source: 'client', priority: 1, clientId };
        setCachedStyle(state, cacheKey, result);
        return result;
      }
    }

    // Priority 2: Try to get template-specific style
    if (templateId) {
      const templateStyle = await getTemplateStyle(client, logger, templateId);
      if (templateStyle && isValidStyle(templateStyle)) {
        const result = { ...templateStyle, source: 'template', priority: 2, templateId };
        setCachedStyle(state, cacheKey, result);
        return result;
      }
    }

    // Priority 3: Try to get user's default style
    if (userId) {
      const defaultStyle = await getDefaultStyle(client, logger, userId);
      if (defaultStyle && isValidStyle(defaultStyle)) {
        const result = { ...defaultStyle, source: 'default', priority: 3, userId };
        setCachedStyle(state, cacheKey, result);
        return result;
      }
    }

    // Priority 4: Return system fallback style
    const fallbackResult = { ...SYSTEM_FALLBACK_STYLE, source: 'system', priority: 4 };
    setCachedStyle(state, cacheKey, fallbackResult);
    return fallbackResult;
  } catch (error) {
    logger.error('Error resolving communication style:', error);
    return { ...SYSTEM_FALLBACK_STYLE, source: 'system', priority: 4, error: error.message };
  }
}

/**
 * Get client-specific communication style
 */
export async function getClientStyle(client, logger, clientId) {
  try {
    const response = await client.graphql({
      query: `
          query GetClient($id: ID!) {
            getClient(id: $id) {
              id
              communicationStyle {
                tone
                formality
                confidence
                personalTouch
                technicalLevel
                sentenceComplexity
                commonPhrases
                signatureGreeting
                signatureClosing
                analyzedFrom
                lastUpdated
              }
            }
          }
        `,
      variables: { id: clientId }
    });
    return response.data?.getClient?.communicationStyle || null;
  } catch (error) {
    logger.error('Error fetching client style:', error);
    return null;
  }
}

/**
 * Get template-specific communication style
 */
export async function getTemplateStyle(client, logger, templateId) {
  try {
    const response = await client.graphql({
      query: `
          query GetNewsletterTemplate($id: ID!) {
            getNewsletterTemplate(id: $id) {
              id
              communicationStyle {
                tone
                formality
                confidence
                personalTouch
                technicalLevel
                sentenceComplexity
                commonPhrases
                signatureGreeting
                signatureClosing
                analyzedFrom
                lastUpdated
              }
            }
          }
        `,
      variables: { id: templateId }
    });
    return response.data?.getNewsletterTemplate?.communicationStyle || null;
  } catch (error) {
    logger.error('Error fetching template style:', error);
    return null;
  }
}

/**
 * Get user's default communication style
 */
export async function getDefaultStyle(client, logger, userId) {
  try {
    const response = await client.graphql({
      query: `
          query GetUserProfile($userId: ID!) {
            listUserProfiles(filter: { userId: { eq: $userId } }) {
              items {
                id
                communicationStyle {
                  tone
                  formality
                  confidence
                  personalTouch
                  technicalLevel
                  sentenceComplexity
                  commonPhrases
                  signatureGreeting
                  signatureClosing
                  analyzedFrom
                  lastUpdated
                }
              }
            }
          }
        `,
      variables: { userId }
    });
    const profiles = response.data?.listUserProfiles?.items || [];
    return profiles[0]?.communicationStyle || null;
  } catch (error) {
    logger.error('Error fetching default style:', error);
    return null;
  }
}

// ─── Style instructions ─────────────────────────────────────────────────────

/**
 * Generate style instructions for AI content generation
 */
export function generateStyleInstructions(style) {
  const instructions = [];

  if (style.tone) {
    const toneMap = {
      professional: 'Use a professional, authoritative tone',
      conversational: 'Use a friendly, conversational tone',
      educational: 'Use an educational, informative tone'
    };
    instructions.push(toneMap[style.tone] || `Use a ${style.tone} tone`);
  }

  if (style.formality) {
    const formalityMap = {
      formal: 'Maintain formal language and structure',
      balanced: 'Use a balanced mix of formal and casual language',
      casual: 'Use casual, approachable language'
    };
    instructions.push(formalityMap[style.formality] || `Be ${style.formality}`);
  }

  if (style.confidence) {
    const confidenceMap = {
      confident: 'Express opinions and recommendations with confidence',
      balanced: 'Balance confidence with appropriate caveats',
      cautious: 'Be cautious and include appropriate disclaimers'
    };
    instructions.push(confidenceMap[style.confidence]);
  }

  if (style.personalTouch) {
    const touchMap = {
      high: 'Include personal anecdotes and direct references to the reader',
      medium: 'Include some personal touches while maintaining professionalism',
      low: 'Maintain professional distance with minimal personal references'
    };
    instructions.push(touchMap[style.personalTouch]);
  }

  if (style.technicalLevel) {
    const techMap = {
      advanced: 'Use technical terms and assume advanced knowledge',
      intermediate: 'Balance technical content with clear explanations',
      basic: 'Avoid jargon and explain all technical concepts simply'
    };
    instructions.push(techMap[style.technicalLevel]);
  }

  if (style.sentenceComplexity) {
    const complexityMap = {
      complex: 'Use varied sentence structures including complex sentences',
      moderate: 'Use a mix of simple and complex sentences',
      simple: 'Keep sentences short and simple'
    };
    instructions.push(complexityMap[style.sentenceComplexity]);
  }

  return instructions;
}

/**
 * Validate if a style object has required fields
 */
export function isValidStyle(style) {
  return style && style.tone && style.formality && style.confidence;
}

// ─── Cache management ───────────────────────────────────────────────────────

/**
 * `state` is `{ cache: Map, cacheExpiry: number }` owned by the facade
 * (per-instance so concurrent services don't share their cache).
 */
export function getCachedStyle(state, key) {
  const cached = state.cache.get(key);
  if (cached && Date.now() - cached.timestamp < state.cacheExpiry) {
    return cached.style;
  }
  state.cache.delete(key);
  return null;
}

export function setCachedStyle(state, key, style) {
  // Evict oldest entries if cache exceeds limit
  if (state.cache.size >= 50) {
    const firstKey = state.cache.keys().next().value;
    state.cache.delete(firstKey);
  }
  state.cache.set(key, { style, timestamp: Date.now() });
}

export function clearCacheForClient(state, clientId) {
  for (const key of state.cache.keys()) {
    if (key.startsWith(clientId)) {
      state.cache.delete(key);
    }
  }
}

export function clearCacheForTemplate(state, templateId) {
  for (const key of state.cache.keys()) {
    if (key.includes(templateId)) {
      state.cache.delete(key);
    }
  }
}

export function clearCacheForUser(state, userId) {
  for (const key of state.cache.keys()) {
    if (key.endsWith(userId)) {
      state.cache.delete(key);
    }
  }
}

export function clearAllCache(state) {
  state.cache.clear();
}

// ─── Methods merged from WritingStyleService ────────────────────────────────

/**
 * Analyze writing sample using AI
 *
 * @param {Object} aiService - injected AI service (instance default if null)
 * @param {Object} logger - logger for failure path
 * @param {string} sample - text to analyze
 * @param {Object|null} overrideAIService - optional override (back-compat
 *   with existing call sites that pass an aiService as the second arg)
 */
export async function analyzeWritingSample(aiService, logger, sample, overrideAIService = null) {
  // Normalise input: accept either a single string or an array of strings.
  // Empty / whitespace-only entries are dropped so we never burn an AI call
  // on noise. With nothing left to analyse we return the default signature
  // rather than asking the model to invent style features from emptiness.
  const samples = Array.isArray(sample)
    ? sample.filter((s) => typeof s === 'string' && s.trim().length > 0)
    : (typeof sample === 'string' && sample.trim().length > 0 ? [sample] : []);

  if (samples.length === 0) {
    return getDefaultStyleSignature();
  }

  // Numbered, clearly delimited block so the model treats each sample as a
  // distinct example rather than one long passage. Single-sample input is
  // numbered too (cheap, keeps the prompt format uniform).
  const sampleBlock = samples
    .map((s, i) => `Sample ${i + 1}:\n${s}`)
    .join('\n\n---\n\n');

  const prompt = `
Analyze the following ${samples.length === 1 ? 'writing sample' : `${samples.length} writing samples`} and extract the writing style characteristics common across them:

${sampleBlock}

Provide analysis in JSON format:
{
  "vocabulary_level": "simple|intermediate|advanced",
  "sentence_structure": "short|varied|complex",
  "tone_markers": ["formal", "friendly", etc.],
  "common_phrases": ["phrase1", "phrase2"],
  "punctuation_style": "minimal|moderate|expressive",
  "paragraph_length": "short|medium|long",
  "key_characteristics": ["characteristic1", "characteristic2"]
}
    `.trim();

  try {
    const service = overrideAIService || aiService;
    if (!service) {
      // #174 follow-up: throw instead of silently falling back. Caller
      // (StyleLearningSection) catches and surfaces an inline error so the
      // user understands why the analysis failed instead of seeing a
      // misleading "your style is too neutral" banner.
      throw new Error('AI service not available for style analysis. Reload the page and try again.');
    }
    const result = await service.analyzeStyle(prompt);

    try {
      return JSON.parse(result);
    } catch (parseError) {
      const jsonMatch = result.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw parseError;
    }
  } catch (error) {
    logger.error('Failed to analyze writing sample:', error);
    // Re-throw so the UI can surface the real cause. Callers that prefer
    // the previous silent-fallback behavior can catch and call
    // getDefaultStyleSignature() themselves.
    throw error;
  }
}

/**
 * Build style prompt for AI generation
 */
export function buildStylePrompt(preferences) {
  if (!preferences) {
    return getDefaultStylePrompt();
  }

  let prompt = `You are generating a financial newsletter. `;
  prompt += `Write in a ${preferences.style || 'professional'} style with a ${preferences.tone || 'professional'} tone. `;

  const complexityGuide = {
    'beginner': 'Use simple language, explain all financial terms, avoid jargon.',
    'intermediate': 'Use standard financial terminology, brief explanations for complex concepts.',
    'advanced': 'Use sophisticated financial language, assume reader knowledge.'
  };
  prompt += complexityGuide[preferences.complexityLevel || 'intermediate'] + ' ';

  if (preferences.styleSignature) {
    const sig = preferences.styleSignature;
    prompt += `\n\nStyle Guidelines:\n`;
    prompt += `- Sentence structure: ${sig.sentence_structure}\n`;
    prompt += `- Vocabulary: ${sig.vocabulary_level}\n`;
    prompt += `- Key characteristics: ${sig.key_characteristics?.join(', ')}\n`;

    if (sig.common_phrases?.length > 0) {
      prompt += `- Incorporate phrases like: ${sig.common_phrases.slice(0, 3).join(', ')}\n`;
    }
  }

  if (preferences.customGuidelines) {
    prompt += `\nAdditional guidelines: ${preferences.customGuidelines}`;
  }

  return prompt;
}

/**
 * Get default style signature
 */
export function getDefaultStyleSignature() {
  return {
    vocabulary_level: 'intermediate',
    sentence_structure: 'varied',
    tone_markers: ['professional', 'informative'],
    common_phrases: [],
    punctuation_style: 'moderate',
    paragraph_length: 'medium',
    key_characteristics: ['clear', 'professional', 'informative']
  };
}

/**
 * Get default style prompt
 */
export function getDefaultStylePrompt() {
  return `You are generating a financial newsletter. Write in a professional style with a professional tone. Use standard financial terminology, brief explanations for complex concepts.`;
}
